import React, { useState, useEffect } from 'react';
import { MapPin, CheckCircle, AlertCircle, Users, Clock, Shield, Fingerprint } from 'lucide-react';
import { api } from '../utils/api';
import { getCurrentLocation } from '../utils/helpers';
import Swal from 'sweetalert2';

// นำเข้า Capacitor และ NativeBiometric
import { Capacitor } from '@capacitor/core';
import { NativeBiometric } from 'capacitor-native-biometric';

// Helper functions for WebAuthn
const base64urlToBuffer = (base64url) => {
  const padding = '='.repeat((4 - base64url.length % 4) % 4);
  const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer;
};

const bufferToBase64url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = '';
  for (let charCode of bytes) {
    str += String.fromCharCode(charCode);
  }
  return window.btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

const FaceScanner = ({ user, appUpdateStatus = 'ok' }) => {
  
  const [isScanning, setIsScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [location, setLocation] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date()); 
  const [wfhMode, setWfhMode] = useState(false);
  const [isWfhChecked, setIsWfhChecked] = useState(false);
  
  const maxDevices = 2;
  const boundEmployeeId = localStorage.getItem('device_bound_to_employee');
  const isCurrentDeviceBound = boundEmployeeId === user?.employee_id;
  const isBoundToAnotherUser = boundEmployeeId && boundEmployeeId !== user?.employee_id;

  const [realPasskeyCount, setRealPasskeyCount] = useState(
    user?.passkey_count !== undefined ? user.passkey_count : (user?.has_passkey ? 1 : 0)
  );

  const [isRegistered, setIsRegistered] = useState(() => {
    return isCurrentDeviceBound; 
  });
  
  const [hasAutoTriggered, setHasAutoTriggered] = useState(false);

  useEffect(() => {
    if (!user?.employee_id) return;
    const fetchFreshPasskeys = async () => {
      try {
        const response = await api.getPasskeys(user.employee_id);
        if (response && response.success) {
          setRealPasskeyCount(response.devices ? response.devices.length : 0);
        }
      } catch (error) {
        console.error('Error fetching passkeys count:', error);
      }
    };
    fetchFreshPasskeys();
  }, [user]);

  const isWrongDevice = isBoundToAnotherUser || (!isCurrentDeviceBound && realPasskeyCount >= maxDevices);

  useEffect(() => {
    loadCurrentLocation();
    checkWfhMode();
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const checkWfhMode = async () => {
    try {
      const data = await api.getOrganizations();
      if (data.length > 0 && data[0].allow_wfh == 1) {
        setWfhMode(true);
      } else {
        setWfhMode(false);
      }
    } catch (error) {
      console.error('Error checking WFH mode:', error);
    } finally {
      setIsWfhChecked(true); 
    }
  };

  const loadCurrentLocation = async () => {
    setLocation({ loading: true });
    try {
      const locationData = await getCurrentLocation();
      setLocation({ ...locationData, loading: false });
    } catch (error) {
      setLocation({ 
        loading: false, 
        error: true,
        message: 'ไม่สามารถรับตำแหน่งได้ กรุณาเปิด GPS'
      });
    }
  };

  useEffect(() => {
    if (appUpdateStatus === 'checking' || appUpdateStatus === 'required') {
      return;
    }

    if (isRegistered && !hasAutoTriggered && !isScanning && !result && isWfhChecked) {
      const isLocationReady = wfhMode || (
        location && 
        !location.loading && 
        !location.error && 
        location.accuracy <= 100 && 
        !location.skipped && 
        !location.manual && 
        location.latitude !== 0
      );
      
      if (isLocationReady) {
        setHasAutoTriggered(true);
        handleWebAuthnScan();
      }
    }
  }, [isRegistered, hasAutoTriggered, wfhMode, location, isScanning, result, isWfhChecked, appUpdateStatus]);

  const handleRegisterDevice = async () => {
    try {
      setIsScanning(true);

      if (Capacitor.isNativePlatform()) {
        const check = await NativeBiometric.isAvailable();
        if (!check.isAvailable) {
          throw new Error('DEVICE_NOT_SECURE');
        }

        try {
          await NativeBiometric.verifyIdentity({
            reason: "ยืนยันตัวตนเพื่อลงทะเบียนอุปกรณ์นี้",
            title: "ลงทะเบียนอุปกรณ์",
          });
        } catch (verifyError) {
          throw new Error('USER_CANCELLED');
        }

        const credentialData = {
          employee_id: user.employee_id,
          credential_id: 'native_device_' + user.employee_id,
          public_key: 'native_auth_bypass', 
          client_data: 'capacitor_app'
        };
        await api.savePasskey(credentialData);

      } else {
        if (!window.PublicKeyCredential) {
          throw new Error('BROWSER_NOT_SUPPORTED');
        }

        const options = await api.getWebAuthnRegistrationOptions(user.employee_id);
        options.challenge = base64urlToBuffer(options.challenge);
        options.user.id = base64urlToBuffer(options.user.id);

        const credential = await navigator.credentials.create({ publicKey: options });

        const credentialData = {
          employee_id: user.employee_id,
          credential_id: credential.id,
          public_key: bufferToBase64url(credential.response.attestationObject), 
          client_data: bufferToBase64url(credential.response.clientDataJSON)
        };
        await api.savePasskey(credentialData);
      }

      localStorage.setItem('device_bound_to_employee', user.employee_id);
      setIsRegistered(true);

      Swal.fire({
        title: 'ลงทะเบียนสำเร็จ!',
        text: 'อุปกรณ์นี้ผูกกับบัญชีของคุณแล้ว สามารถสแกนเพื่อลงเวลาได้ทันที',
        icon: 'success',
        confirmButtonColor: '#3B82F6',
        customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl px-6 py-2 font-bold' }
      });
      
    } catch (error) {
      console.error('Registration failed:', error);
      
      let errorTitle = 'การลงทะเบียนล้มเหลว';
      let errorMessage = 'เกิดข้อผิดพลาดในการลงทะเบียน กรุณาลองใหม่อีกครั้ง';

      if (error.message === 'DEVICE_NOT_SECURE') {
        errorMessage = 'อุปกรณ์ของคุณยังไม่ได้ตั้งค่ารหัสล็อคหน้าจอ หรือไม่ได้บันทึกลายนิ้วมือ/ใบหน้าไว้ กรุณาไปที่ "การตั้งค่า" ของเครื่องเพื่อเปิดใช้งานก่อน';
      } else if (error.message === 'USER_CANCELLED' || error.name === 'NotAllowedError') {
        errorMessage = 'คุณยกเลิกการทำรายการ หรือไม่อนุญาตให้สแกน';
      } else if (error.message === 'BROWSER_NOT_SUPPORTED') {
        errorMessage = 'อุปกรณ์หรือบราวเซอร์ของคุณไม่รองรับระบบ Passkey';
      } else if (error.message) {
        errorMessage = error.message; 
      }

      Swal.fire({
        title: errorTitle,
        text: errorMessage,
        icon: 'error',
        confirmButtonColor: '#EF4444'
      });
    } finally {
      setIsScanning(false);
    }
  };

  const handleWebAuthnScan = async () => {
    if (!wfhMode) {
      if (!location || location.loading) {
        Swal.fire('รอดำเนินการ', 'กรุณารอระบบหาตำแหน่งของคุณให้เสร็จก่อน', 'info');
        if (!location) loadCurrentLocation();
        return;
      }
      if (location.error) {
        Swal.fire({
          title: 'เกิดข้อผิดพลาด', 
          html: 'ไม่สามารถรับตำแหน่ง GPS ได้<br>กรุณา: เปิด GPS และอนุญาตให้เบราว์เซอร์เข้าถึงตำแหน่ง', 
          icon: 'error'
        });
        loadCurrentLocation();
        return;
      }
      if (location.skipped || location.manual || location.latitude === 0) {
        Swal.fire('แจ้งเตือน', 'ระบบต้องการตำแหน่งที่แม่นยำเพื่อบันทึกเวลา กรุณาเปิด GPS', 'warning');
        loadCurrentLocation();
        return;
      }
      if (location.accuracy && location.accuracy > 100) {
        Swal.fire({
          title: 'ตำแหน่งไม่แม่นยำ', 
          html: `ความแม่นยำของตำแหน่งต่ำเกินไป (±${Math.round(location.accuracy)}m)<br><br>กรุณา: ไปที่บริเวณโล่ง แล้วลองอีกครั้ง`, 
          icon: 'warning'
        });
        return;
      }
    }

    try {
      setIsScanning(true);

      if (Capacitor.isNativePlatform()) {
        const check = await NativeBiometric.isAvailable();
        if (!check.isAvailable) {
          throw new Error('DEVICE_NOT_SECURE');
        }

        try {
          await NativeBiometric.verifyIdentity({
            reason: "สแกนนิ้ว/ใบหน้า เพื่อลงเวลาเข้า-ออกงาน",
            title: "ลงเวลาทำงาน",
          });
        } catch (verifyError) {
          throw new Error('USER_CANCELLED');
        }

      } else {
        if (!window.PublicKeyCredential) {
          throw new Error('BROWSER_NOT_SUPPORTED');
        }

        const options = await api.getWebAuthnAuthOptions(user.employee_id);
        options.challenge = base64urlToBuffer(options.challenge);
        if (options.allowCredentials) {
          options.allowCredentials.forEach(cred => cred.id = base64urlToBuffer(cred.id));
        }
        await navigator.credentials.get({ publicKey: options });
      }

      const requestData = {
        face_descriptor: [], 
        location: wfhMode ? { wfh_mode: true, latitude: 0, longitude: 0 } : location,
        employee_id: user.employee_id,
        confirmed: true, 
        captured_image: null 
      };

      const data = await api.recognizeFace(requestData);
      setResult(data);
      
    } catch (error) {
      console.error(error);
      
      let errorMsg = 'เกิดข้อผิดพลาดในการตรวจสอบ กรุณาลองใหม่อีกครั้ง';
      
      if (error.message === 'DEVICE_NOT_SECURE') {
        errorMsg = 'ไม่พบการตั้งค่าความปลอดภัยในเครื่อง กรุณาตั้งค่ารหัสล็อคหน้าจอหรือลายนิ้วมือก่อนลงเวลา';
      } else if (error.message === 'USER_CANCELLED' || error.name === 'NotAllowedError') {
        errorMsg = 'คุณยกเลิกการสแกน หรือใช้เวลาสแกนนานเกินไป';
      } else if (error.message === 'BROWSER_NOT_SUPPORTED') {
        errorMsg = 'เบราว์เซอร์ไม่รองรับระบบตรวจสอบนี้';
      }

      Swal.fire({
        title: 'สแกนไม่สำเร็จ',
        text: errorMsg,
        icon: 'warning',
        confirmButtonText: 'ตกลง',
        confirmButtonColor: '#3B82F6',
        customClass: { popup: 'rounded-2xl', confirmButton: 'rounded-xl px-6 font-bold' }
      });
    } finally {
      setIsScanning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">

      <div className="max-w-md mx-auto px-4 py-6 space-y-6">
        
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <MapPin className={`h-6 w-6 mx-auto mb-2 ${wfhMode ? 'text-indigo-500' : (location && !location.loading && !location.error ? 'text-blue-500' : 'text-gray-400')}`} />
            <div className="text-xs font-medium text-gray-600">{wfhMode ? 'WFH Mode' : 'Location'}</div>
            <div className={`text-sm font-bold ${wfhMode ? 'text-indigo-600' : (location && !location.loading && !location.error ? 'text-blue-600' : 'text-gray-500')}`}>
              {wfhMode ? 'Active' : (location && location.loading ? 'Finding...' : 
               location && !location.loading && !location.error ? 'Ready' : 
               location && location.error ? 'Error' : 'Waiting')}
            </div>
          </div>
          
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 text-center shadow-lg border border-white/20">
            <Clock className="h-6 w-6 mx-auto mb-2 text-indigo-500" />
            <div className="text-xs font-medium text-gray-600">Time</div>
            <div className="text-sm font-bold text-indigo-600">
              {currentTime.toLocaleTimeString('th-TH', { 
                hour: '2-digit', 
                minute: '2-digit', 
                second: '2-digit' 
              })}
            </div>
          </div>
        </div>

        <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 overflow-hidden relative">
          
          <div className="relative overflow-hidden bg-white/50 flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in duration-300">
            
            {isWrongDevice ? (
              <>
                <div className="relative mb-6">
                  <div className="w-28 h-28 bg-red-100 text-red-600 rounded-full flex items-center justify-center shadow-inner">
                    <AlertCircle className="h-16 w-16" />
                  </div>
                </div>
                <h3 className="text-2xl font-extrabold text-red-600 mb-2">
                  อุปกรณ์ไม่ถูกต้อง
                </h3>
                <p className="text-gray-500 mb-8 text-sm">
                  กรุณาใช้อุปกรณ์หรือเบราว์เซอร์ที่ลงทะเบียนไว้<br />
                  หากท่านจำไม่ได้ให้ไปที่หน้าโปรไฟล์ หรือ ติดต่อแอดมิน
                </p>
                <button
                  disabled
                  className="w-[85%] bg-gray-300 text-gray-500 font-bold py-4 rounded-2xl shadow-none mb-6 cursor-not-allowed"
                >
                  ไม่สามารถลงเวลาได้
                </button>
              </>
            ) : !isRegistered ? (
              <>
                <div className="relative mb-6">
                  <div className={`w-28 h-28 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shadow-inner ${isScanning ? 'animate-pulse' : ''}`}>
                    <Fingerprint className="h-16 w-16" />
                  </div>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-800 mb-2">ลงทะเบียนไบโอเมทริกซ์</h3>
                <p className="text-gray-500 mb-8 text-sm">
                  กรุณาลงทะเบียนลายนิ้วมือ/ใบหน้ากับอุปกรณ์นี้ <br/>ก่อนเริ่มใช้งานลงเวลา
                </p>
                <button
                  onClick={handleRegisterDevice}
                  disabled={isScanning}
                  className="w-full bg-blue-600 text-white font-bold py-4 rounded-2xl shadow-xl hover:bg-blue-700 transition-all mb-2 disabled:opacity-50"
                >
                  {isScanning ? 'กำลังดำเนินการ...' : 'ลงทะเบียนอุปกรณ์นี้'}
                </button>
              </>
            ) : (
              <>
                <div className="relative mb-6">
                  <div className={`w-28 h-28 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center shadow-inner ${isScanning ? 'animate-pulse' : ''}`}>
                    <Fingerprint className="h-16 w-16" />
                  </div>
                </div>
                <h3 className="text-2xl font-extrabold text-gray-800 mb-2">ลงชื่อด้วยไบโอเมทริกซ์ (Biometrics)</h3>
                
                <p className="text-gray-500 mb-8 text-sm">
                  {!isWfhChecked ? 'กำลังตรวจสอบสิทธิ์ WFH...' :
                   !wfhMode && (!location || location.loading) ? 'กำลังตรวจสอบพิกัด GPS...' :
                   !wfhMode && location?.error ? 'ไม่สามารถระบุตำแหน่งพิกัดได้' :
                   !wfhMode && location?.accuracy > 100 ? 'รอสัญญาณ GPS ให้แม่นยำขึ้น...' :
                   'กรุณาสแกนใบหน้า/นิ้วมือ เพื่อเข้างาน'}
                </p>

                <button
                  onClick={() => {
                    if (result) {
                      setResult(null); 
                      setTimeout(() => handleWebAuthnScan(), 100); 
                    } else {
                      handleWebAuthnScan();
                    }
                  }}
                  disabled={isScanning || !isWfhChecked || (!wfhMode && location?.loading)}
                  className="w-[85%] bg-blue-600 text-white font-bold py-4 rounded-2xl shadow-xl hover:bg-blue-700 transition-all mb-6 disabled:opacity-50"
                >
                  {isScanning ? 'กำลังตรวจสอบ...' : 'กดเพื่อสแกนอีกครั้ง'}
                </button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-2">
            {/* ปรับปรุงปุ่มรีเฟรชตำแหน่งตรงนี้ */}
            {!wfhMode && (
              <button
                onClick={loadCurrentLocation}
                disabled={location?.loading}
                className={`w-full ${location?.loading ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:shadow-xl hover:scale-105'} text-white py-3 px-6 rounded-2xl font-bold shadow-lg transform transition-all duration-200 flex items-center justify-center space-x-2`}
              >
                <MapPin className={`h-5 w-5 ${location?.loading ? 'animate-pulse' : ''}`} />
                <span>{location?.loading ? 'กำลังอัปเดตพิกัด...' : 'รีเฟรชตำแหน่ง'}</span>
              </button>
            )}

            {result && (
              <div className="space-y-4 animate-in slide-in-from-bottom-4 duration-300">
                <div className={`p-6 rounded-2xl ${result.success ? 'bg-green-50 border-2 border-green-200' : 'bg-red-50 border-2 border-red-200'}`}>
                  <div className={`flex flex-col items-center justify-center text-center ${result.success ? 'text-green-800' : 'text-red-800'}`}>
                    <div className="flex items-center justify-center space-x-3 w-full">
                      {result.success ? (
                        <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <CheckCircle className="h-7 w-7 text-white" />
                        </div>
                      ) : (
                        <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                          <AlertCircle className="h-7 w-7 text-white" />
                        </div>
                      )}
                      <p className="text-xl font-bold">{result.message || result.error}</p>
                    </div>
                    
                    <div className="w-full mt-4">
                      {result.success && result.employee && (
                        <div className="space-y-1.5 flex flex-col items-center">
                          <div className="flex items-center justify-center space-x-2">
                            <Users className="h-4 w-4" />
                            <span className="font-medium">{result.employee.first_name} {result.employee.last_name}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${result.action === 'check_in' ? 'bg-blue-500' : 'bg-orange-500'}`}></div>
                            <span className="text-sm">{result.action === 'check_in' ? 'เข้างาน' : 'ออกงาน'}</span>
                          </div>
                          <div className="flex items-center justify-center space-x-2">
                            <Clock className="h-4 w-4" />
                            <span className="text-sm">{new Date().toLocaleString('th-TH')}</span>
                          </div>
                        </div>
                      )}

                      {!result.success && (
                        <div className="text-sm flex flex-col items-center text-red-700/80 space-y-1">
                          {result.distance && (
                            <p>ระยะห่าง: {result.distance} (สูงสุด: {result.max_distance})</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
        </div>
        
        {!wfhMode && location && !location.loading && !location.error && (
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-3 text-sm">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-blue-600" />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900">Current Location</p>
                <p className="text-gray-600 truncate">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                  {location.accuracy && (
                    <span className="text-gray-500"> (±{Math.round(location.accuracy)}m)</span>
                  )}
                </p>
              </div>
              <div className="flex-shrink-0">
                <div className={`w-3 h-3 rounded-full ${
                  location.accuracy <= 50 ? 'bg-green-500' : 
                  location.accuracy <= 100 ? 'bg-yellow-500' : 
                  'bg-red-500'
                }`}></div>
              </div>
            </div>
            {location.accuracy > 50 && (
              <div className="mt-3 p-3 bg-yellow-50 rounded-xl border border-yellow-200">
                <p className="text-xs text-yellow-700 flex items-center">
                  <AlertCircle className="h-3 w-3 mr-1" />
                  {location.accuracy > 100 ? 
                    'ความแม่นยำของตำแหน่งต่ำ กรุณาไปที่บริเวณโล่ง' : 
                    'ความแม่นยำปานกลาง แนะนำให้ไปที่บริเวณโล่ง'}
                </p>
              </div>
            )}
          </div>
        )}

        {wfhMode && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-4 shadow-lg border border-indigo-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
                <MapPin className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-bold text-indigo-900">โหมด Work From Home</p>
                <p className="text-sm text-indigo-700">ไม่ต้องตรวจสอบตำแหน่ง GPS</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FaceScanner;